/**
 * Dialer App — Issue 24: Shell + capability token authentication
 *
 * Responsibilities:
 *  1. Auth guard: checks Supabase session on load; redirects to dashboard login if none.
 *  2. Fetches the user's assigned phone numbers (GET /dialer/numbers).
 *  3. Fetches a capability token (GET /dialer/capability-token) and initialises the AT SDK.
 *  4. Renders the persistent shell: DialerHeader, tab screens, ActiveCallBar, TabBar.
 *  5. All five tab screens are always mounted — only visibility changes on tab switch
 *     so an active call is never interrupted by navigating between tabs.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { PhoneOff } from "lucide-react";

import { supabase } from "./lib/supabase";
import { useATClient, type IncomingCallInfo } from "./hooks/useATClient";
import { TabBar, type TabId } from "./components/TabBar";
import { DialerHeader, type DialerNumber } from "./components/DialerHeader";
import { ErrorBanner } from "./components/ErrorBanner";
import { NetworkBanner } from "./components/NetworkBanner";
import { DialpadScreen, COUNTRIES } from "./screens/DialpadScreen";
import { ActiveCallScreen } from "./screens/ActiveCallScreen";
import { IncomingCallScreen } from "./screens/IncomingCallScreen";
import { PostCallScreen } from "./screens/PostCallScreen";
import { CallHistoryScreen } from "./screens/CallHistoryScreen";
import { ContactsScreen } from "./screens/ContactsScreen";
import { MessagesScreen } from "./screens/MessagesScreen";
import { ProfileScreen } from "./screens/ProfileScreen";
import { BlockedScreen } from "./screens/BlockedScreen";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:4000";
const DASHBOARD_URL =
  (import.meta.env.VITE_DASHBOARD_URL as string | undefined) ?? "http://localhost:3000";

/**
 * Clears the local Supabase session and sends the user to the dashboard login
 * page with a redirect_to param so they land back on the dialer after signing in.
 * Called on 401/403 API responses and on failed visibility-change session checks.
 */
async function signOutAndRedirect(): Promise<void> {
  await supabase.auth.signOut({ scope: "local" });
  const redirectTo = encodeURIComponent(window.location.origin);
  window.location.href = `${DASHBOARD_URL}/login?redirect_to=${redirectTo}`;
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [profileFirstName, setProfileFirstName] = useState<string | null>(null);
  const [profileLastName, setProfileLastName] = useState<string | null>(null);
  const [profileRole, setProfileRole] = useState<string | null>(null);
  const [planStatus, setPlanStatus] = useState<string | null>(null);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [wrapupTimeSeconds, setWrapupTimeSeconds] = useState(600);
  const [numbers, setNumbers] = useState<DialerNumber[]>([]);
  const [selectedNumberId, setSelectedNumberId] = useState<string | null>(null);
  const [capToken, setCapToken] = useState<string | null>(null);
  const [tokenExpiresIn, setTokenExpiresIn] = useState(86_400);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("dialpad");
  // Dialpad input — lifted here so TabBar's centre button can read + trigger calls.
  const [dialValue, setDialValue] = useState("");
  const [dialCountryCode, setDialCountryCode] = useState("+234"); // Nigeria default
  const initRef = useRef(false);
  // DNC confirmation modal state — set when the dialed number belongs to a
  // contact with do_not_call = true.  The user must confirm before the call proceeds.
  const [dncConfirm, setDncConfirm] = useState<{
    contactName: string;
    callNumber: string;
  } | null>(null);
  // Track whether the SDK has ever reached "ready" so we know whether a
  // subsequent "initialising" state is a reconnect (show banner) vs the initial
  // app load (don't flash a banner for the first ~2 seconds).
  const sdkEverReadyRef = useRef(false);

  // Parse an E.164 number against the known country codes list.
  // Tries longest codes first to avoid false partial matches (e.g. +2 before +234).
  // Returns { countryCode, localNumber } or null if no code matched.
  const parseCallNumber = (raw: string): { countryCode: string; localNumber: string } | null => {
    const sorted = [...COUNTRIES].sort((a, b) => b.code.length - a.code.length);
    for (const country of sorted) {
      if (raw.startsWith(country.code)) {
        return { countryCode: country.code, localNumber: raw.slice(country.code.length) };
      }
    }
    return null;
  };

  // Read ?callNumber from the URL on first load and pre-fill the dialpad.
  // This is set by the dashboard "Call" button on the contact detail page.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const callNumber = params.get("callNumber");
    if (!callNumber) return;

    const parsed = parseCallNumber(callNumber);
    if (parsed) {
      setDialCountryCode(parsed.countryCode);
      setDialValue(parsed.localNumber);
    } else {
      // Unknown country code — drop the + and put the full number in the field.
      setDialValue(callNumber.replace(/^\+/, ""));
    }
    setActiveTab("dialpad");

    // Remove the param from the URL so a page refresh doesn't re-prefill.
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState(null, "", cleanUrl);
  }, []); // intentionally runs once on mount only

  // 1. Session check — redirect to dashboard login if unauthenticated.
  //
  // When the user opens the dialer via the dashboard "Open Dialer" button, the
  // dashboard appends the Supabase session tokens to the URL hash:
  //   phone.closecall.io#access_token=...&refresh_token=...
  //
  // localStorage is scoped per origin, so the dialer starts with no session.
  // We hydrate it by calling setSession() with the hash tokens first, then
  // immediately clean the hash from the URL so tokens aren't visible or logged.
  useEffect(() => {
    const initAuth = async () => {
      // Try to hydrate session from URL hash (set by dashboard "Open Dialer").
      const rawHash = window.location.hash.slice(1); // strip leading #
      if (rawHash) {
        const params = new URLSearchParams(rawHash);
        const accessToken = params.get("access_token");
        const refreshToken = params.get("refresh_token");
        if (accessToken && refreshToken) {
          await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          // Remove tokens from the URL immediately for security.
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
        }
      }

      const {
        data: { session: s },
      } = await supabase.auth.getSession();

      // getSession() reads from localStorage — it trusts whatever is cached.
      // getUser() validates against the Supabase server, so it catches the case
      // where the dashboard signed out with scope:'global' (server-side revocation)
      // but the dialer's localStorage still holds the old tokens.
      if (s) {
        const { error } = await supabase.auth.getUser();
        if (error) {
          await supabase.auth.signOut({ scope: "local" }); // clear stale local cache
          const redirectTo = encodeURIComponent(window.location.origin);
          window.location.href = `${DASHBOARD_URL}/login?redirect_to=${redirectTo}`;
          return;
        }
      }

      setSession(s);
      setAuthLoading(false);
      if (!s) {
        const redirectTo = encodeURIComponent(window.location.origin);
        window.location.href = `${DASHBOARD_URL}/login?redirect_to=${redirectTo}`;
      }
    };

    void initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (!s) {
        const redirectTo = encodeURIComponent(window.location.origin);
        window.location.href = `${DASHBOARD_URL}/login?redirect_to=${redirectTo}`;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // 1b. Visibility-change guard — fires when the user switches back to this tab.
  //     Because the dashboard and dialer are on different origins, onAuthStateChange
  //     cannot receive the dashboard's sign-out event directly. Instead we ask the
  //     Supabase server "am I still valid?" the moment this tab becomes visible.
  //     If the refresh token was revoked (e.g. global sign-out from the dashboard),
  //     getUser() returns an error and we redirect immediately.
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      const { error } = await supabase.auth.getUser();
      if (error) void signOutAndRedirect();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // 2. Fetch capability token from authenticated endpoint.
  //    phoneNumber — the outbound caller ID to embed in the token. When provided,
  //    AT will use that number as the caller ID for outgoing calls made with this
  //    token. Falls back to AT_PHONE_NUMBER env var on the server when omitted.
  const fetchToken = useCallback(
    async (accessToken: string, phoneNumber?: string): Promise<string | null> => {
      setTokenLoading(true);
      try {
        const url = new URL(`${API_URL}/dialer/capability-token`);
        if (phoneNumber) url.searchParams.set("phoneNumber", phoneNumber);
        const res = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (res.status === 401 || res.status === 403) {
          void signOutAndRedirect();
          return null;
        }
        if (!res.ok) return null;
        const data = (await res.json()) as { token: string; expiresIn: number };
        setTokenExpiresIn(data.expiresIn);
        return data.token;
      } catch {
        return null;
      } finally {
        setTokenLoading(false);
      }
    },
    [],
  );

  // 3. Fetch numbers assigned to this user.
  const fetchNumbers = useCallback(async (accessToken: string) => {
    try {
      const res = await fetch(`${API_URL}/dialer/numbers`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.status === 401 || res.status === 403) {
        void signOutAndRedirect();
        return;
      }
      if (!res.ok) return;
      const data = (await res.json()) as { numbers: DialerNumber[] };
      setNumbers(data.numbers);
      if (data.numbers.length > 0 && data.numbers[0]) {
        setSelectedNumberId(data.numbers[0].id);
      }
    } catch {
      // Numbers are convenience UI — don't block the app if this fails.
    }
  }, []);

  // 4. Fetch profile info for the Profile tab.
  const fetchProfile = useCallback(async (accessToken: string) => {
    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.status === 401 || res.status === 403) {
        void signOutAndRedirect();
        return;
      }
      if (!res.ok) return;
      const data = (await res.json()) as {
        profile?: {
          id?: string;
          firstName?: string;
          lastName?: string;
          role?: string;
          planStatus?: string;
          creditBalance?: number;
        };
      };
      setProfileId(data.profile?.id ?? null);
      setProfileFirstName(data.profile?.firstName ?? null);
      setProfileLastName(data.profile?.lastName ?? null);
      setProfileRole(data.profile?.role ?? null);
      setPlanStatus(data.profile?.planStatus ?? null);
      setCreditBalance(data.profile?.creditBalance ?? null);
    } catch {
      // silently ignore
    }
  }, []);

  // 4b. Fetch account settings for wrapup time.
  const fetchSettings = useCallback(async (accessToken: string) => {
    try {
      const res = await fetch(`${API_URL}/settings`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) return;
      const data = (await res.json()) as { wrapupTimeSeconds?: number };
      if (data.wrapupTimeSeconds) setWrapupTimeSeconds(data.wrapupTimeSeconds);
    } catch {
      // silently ignore — default 600s will be used
    }
  }, []);

  // 5. Kick off all fetches once we have a session.
  useEffect(() => {
    if (!session || initRef.current) return;
    initRef.current = true;
    const t = session.access_token;
    void fetchToken(t).then((tok) => {
      if (tok) setCapToken(tok);
    });
    void fetchNumbers(t);
    void fetchProfile(t);
    void fetchSettings(t);
  }, [session, fetchToken, fetchNumbers, fetchProfile, fetchSettings]);

  // 6a. When the user switches to a different outbound number, re-fetch the
  //     capability token so the AT SDK is reinitialised with the correct caller ID.
  //     We skip the very first assignment (handled by the init effect above).
  // Tracks the phone number string of the last capability token that successfully
  // initialised the AT SDK. refreshToken must use THIS number — not selectedNumberId
  // — so that if a number-switch token fetch fails (e.g. AT rejects the number),
  // auto-reconnect retries with the number that actually works rather than the
  // newly selected (but broken) one.
  const activePhoneNumberRef = useRef<string | undefined>(undefined);

  const prevSelectedNumberIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (selectedNumberId === prevSelectedNumberIdRef.current) return;
    prevSelectedNumberIdRef.current = selectedNumberId;
    if (!session || !selectedNumberId) return;
    const num = numbers.find((n) => n.id === selectedNumberId);
    if (!num) return;
    void fetchToken(session.access_token, num.number).then((tok) => {
      if (tok) {
        setCapToken(tok);
        // Commit this phone number as the active one only on success.
        activePhoneNumberRef.current = num.number;
      }
      // On failure: capToken stays unchanged, activePhoneNumberRef stays unchanged.
      // The SDK keeps running on the previous number's token.
    });
  }, [selectedNumberId, session, numbers, fetchToken]);

  // 6b. Proactive refresh callback passed to useATClient — always uses the last
  //     *successfully committed* phone number, not whatever is currently selected.
  //     This prevents the reconnect loop that occurs when switching to a number
  //     whose token request AT rejects: selectedNumberId changes but the SDK is
  //     still on the old token, so refreshToken must refresh the old number.
  const refreshToken = useCallback(async (): Promise<string | null> => {
    const {
      data: { session: s },
    } = await supabase.auth.getSession();
    if (!s) return null;
    return fetchToken(s.access_token, activePhoneNumberRef.current);
  }, [fetchToken]);

  // 7. AT SDK hook — manages SDK lifecycle, call state, network quality.
  const atClient = useATClient({
    token: capToken,
    expiresIn: tokenExpiresIn,
    refreshToken,
    onInboundAnswered: (sessionId) => {
      // Fire-and-forget: write profile_id onto the call row the moment the rep
      // answers — more reliable than waiting for AT's webhook destinationNumber.
      const token = session?.access_token;
      if (!token) return;
      void fetch(`${API_URL}/calls/session/${sessionId}/answered`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    },
  });

  // Track first "ready" so we can show the network banner on subsequent degraded
  // states without flashing it during the initial app load.
  if (atClient.sdkStatus === "ready" && !sdkEverReadyRef.current) {
    sdkEverReadyRef.current = true;
  }

  // 7a. Presence heartbeat — POST /dialer/presence whenever the SDK status changes,
  //     AND on a 60-second interval to keep last_seen fresh.  The routing handler
  //     treats any row with last_seen older than 3 minutes as stale/offline, so
  //     without this interval a rep sitting idle would disappear from routing.
  const sendPresence = useCallback(() => {
    if (!session) return;
    void fetch(`${API_URL}/dialer/presence`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ sdkStatus: atClient.sdkStatus }),
    });
  }, [session, atClient.sdkStatus]);

  useEffect(() => {
    sendPresence();
  }, [sendPresence]);

  useEffect(() => {
    const id = setInterval(sendPresence, 60_000);
    return () => clearInterval(id);
  }, [sendPresence]);

  // 7a-ii. Send presence immediately when the tab becomes visible again so the
  //        routing handler doesn't have to wait up to 60s for the heartbeat.
  //        Paired with the fast-reconnect in useATClient, this ensures the rep
  //        shows as "available" as quickly as possible after switching tabs.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") sendPresence();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [sendPresence]);

  // 7b. Supabase Realtime subscription — listens for inbound_call broadcasts on
  //     the `inbound:{profileId}` channel.  The server publishes call context
  //     (contact name, tags, etc.) here alongside the AT SDK ring so the rep
  //     sees enriched caller information the moment the phone rings.
  useEffect(() => {
    if (!profileId) return;

    const channel = supabase
      .channel(`inbound:${profileId}`)
      .on("broadcast", { event: "inbound_call" }, ({ payload }) => {
        atClient.setIncomingCallContext(payload as IncomingCallInfo);
      })
      .on("broadcast", { event: "outbound_call_created" }, ({ payload }) => {
        const p = payload as { callId: string };
        if (p.callId) atClient.setOutboundCallId(p.callId);
      })
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
    // atClient.setIncomingCallContext is stable (useCallback with no deps that change).
    // We intentionally only re-subscribe when profileId changes, not on every render.
  }, [profileId]);

  // 7c. Client-side contact lookup for inbound calls — fires the moment a
  //     callerNumber is known so the rep sees the contact name while the phone
  //     rings, without depending on the Supabase Realtime broadcast arriving
  //     in time (the broadcast can take 500ms–1s+ in the server's fire-and-forget).
  const inboundLookupDoneRef = useRef<string | null>(null);
  useEffect(() => {
    const callerNumber = atClient.incomingCall?.callerNumber;

    // Reset on call end so the next call from the same number gets a fresh lookup.
    if (!callerNumber) {
      inboundLookupDoneRef.current = null;
      return;
    }

    // Skip if we already looked up this number (avoid duplicate fetches).
    if (inboundLookupDoneRef.current === callerNumber) return;
    if (!session?.access_token) return;

    inboundLookupDoneRef.current = callerNumber;

    // Capture values we'll need in the async callback (avoids stale closure).
    const sessionId = atClient.incomingCall?.sessionId ?? "";
    const assignedRepId = atClient.incomingCall?.assignedRepId ?? null;
    const accessToken = session.access_token;

    // Cancellation flag — set to true in the cleanup so the fetch callback
    // becomes a no-op if the call ends (or callerNumber changes) before it resolves.
    let cancelled = false;

    void (async () => {
      try {
        const res = await fetch(
          `${API_URL}/contacts?search=${encodeURIComponent(callerNumber)}&limit=1`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (cancelled || !res.ok) return;
        const data = (await res.json()) as {
          contacts: Array<{
            id: string;
            firstName: string | null;
            lastName: string | null;
            tags: string[];
          }>;
        };
        if (cancelled) return;
        const match = data.contacts[0];
        if (!match) return;
        const contactName = [match.firstName, match.lastName].filter(Boolean).join(" ") || null;
        atClient.setIncomingCallContext({
          sessionId,
          callerNumber,
          contactName,
          contactId: match.id,
          contactTags: match.tags ?? [],
          assignedRepId,
          callId: null,
        });
      } catch {
        // silently ignore — contact name will just not show
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [atClient.incomingCall?.callerNumber, session?.access_token]);

  // 8. Single call action for the TabBar centre button.
  //    Prepends the selected country code; strips a leading 0 (Nigerian local format).
  //    Before connecting, checks whether the number belongs to a DNC contact.
  //    If so, opens a confirmation modal and defers the actual call.
  const handleDialCall = useCallback(async () => {
    const local = dialValue.trim().replace(/^0/, "");
    if (!local) return;
    const fullNumber = `${dialCountryCode}${local}`;

    // DNC check — look up the number in contacts.
    if (session?.access_token) {
      try {
        const res = await fetch(
          `${API_URL}/contacts?search=${encodeURIComponent(fullNumber)}&limit=1`,
          { headers: { Authorization: `Bearer ${session.access_token}` } },
        );
        if (res.status === 401 || res.status === 403) {
          void signOutAndRedirect();
          return;
        }
        if (res.ok) {
          const data = (await res.json()) as {
            contacts: Array<{
              firstName: string | null;
              lastName: string | null;
              doNotCall: boolean;
            }>;
          };
          const contact = data.contacts[0];
          if (contact?.doNotCall) {
            const name =
              [contact.firstName, contact.lastName].filter(Boolean).join(" ") || fullNumber;
            setDncConfirm({ contactName: name, callNumber: fullNumber });
            return; // wait for confirmation
          }
        }
      } catch {
        // DNC check failed — proceed with call rather than blocking the user.
      }
    }

    atClient.call(fullNumber);
  }, [dialValue, dialCountryCode, atClient, session]);

  // canDialCall: all three conditions must be true — SDK ready, number typed,
  // AND currently on the dialpad tab. If you're on another tab with a number
  // already typed, the centre button stays in its "navigate to dialpad" state
  // and will NOT initiate a call.
  const canDialCall =
    atClient.sdkStatus === "ready" && dialValue.trim().length > 0 && activeTab === "dialpad";

  // True when an inbound call is ringing and waiting for the rep to respond.
  // Takes priority over the outbound active call screen.
  const isIncomingRinging = atClient.incomingCall !== null && atClient.sdkStatus === "ringing";

  const isActiveCalling = atClient.sdkStatus === "ringing" || atClient.sdkStatus === "incall";

  // Silent audio unlock — Chrome blocks audio.play() calls that originate from
  // WebSocket message handlers (like the AT SDK's internal audio). Resuming an
  // AudioContext inside a real user gesture marks the entire page as audio-allowed,
  // so the SDK's subsequent play() calls succeed. No UI is shown.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const ringtoneRef = useRef<HTMLAudioElement | null>(null);
  // Ref mirror of isIncomingRinging so the resumeAudioCtx callback (no deps) can
  // read the latest value without becoming stale.
  const isIncomingRingingRef = useRef(false);
  isIncomingRingingRef.current = isIncomingRinging;

  const resumeAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      try {
        audioCtxRef.current = new AudioContext();
      } catch {
        /* unsupported */
      }
    }
    if (audioCtxRef.current?.state === "suspended") {
      void audioCtxRef.current.resume().then(() => {
        // Retry the ringtone now that audio is unlocked — it may have been
        // blocked by Chrome's autoplay policy when the call first arrived.
        if (isIncomingRingingRef.current) {
          void ringtoneRef.current?.play().catch(() => {});
        }
      });
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", resumeAudioCtx);
    document.addEventListener("keydown", resumeAudioCtx);
    return () => {
      document.removeEventListener("click", resumeAudioCtx);
      document.removeEventListener("keydown", resumeAudioCtx);
    };
  }, [resumeAudioCtx]);

  // Create the ringtone Audio element once on mount.
  useEffect(() => {
    const audio = new Audio("/sounds/ring.mp3");
    audio.loop = true;
    ringtoneRef.current = audio;
    return () => {
      audio.pause();
      audio.src = "";
    };
  }, []);

  // Play / stop the ringtone as incoming ringing state changes.
  useEffect(() => {
    const audio = ringtoneRef.current;
    if (!audio) return;
    if (isIncomingRinging) {
      void audio.play().catch(() => {
        // Autoplay blocked — resumeAudioCtx will retry on the next user interaction.
      });
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [isIncomingRinging]);

  const handleAnswerIncoming = useCallback(() => {
    resumeAudioCtx(); // last guaranteed user gesture before SDK starts WebRTC audio
    atClient.answer();
  }, [resumeAudioCtx, atClient]);

  // Decline handler: hang up on the AT SDK (which lets AT continue the waterfall
  // to other reps) and fire-and-forget the audit POST.
  const handleDeclineIncoming = useCallback(() => {
    const sessionId = atClient.incomingCall?.sessionId;
    atClient.hangup();
    if (sessionId && session?.access_token) {
      void fetch(`${API_URL}/calls/${sessionId}/decline`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
    }
  }, [atClient, session]);

  // The selected outbound number string (used as "via" in the active call screen).
  const selectedNumber = numbers.find((n) => n.id === selectedNumberId) ?? null;

  // Loading screens — #root already provides bg-background and max-w-[430px].
  if (authLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  if (!session) return null;

  // ── Plan / credit gate ────────────────────────────────────────────────────
  // Only evaluate once the profile has loaded (planStatus !== null).
  // No-plan check takes priority over no-credits check.
  if (planStatus !== null) {
    if (planStatus !== "active") {
      return <BlockedScreen variant="no-plan" dashboardUrl={DASHBOARD_URL} />;
    }
    if (creditBalance !== null && creditBalance <= 0) {
      return <BlockedScreen variant="no-credits" dashboardUrl={DASHBOARD_URL} />;
    }
  }

  if (tokenLoading && !capToken) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Connecting to calling service…</p>
        </div>
      </div>
    );
  }

  // Main shell — #root is the phone-width container; this fills it entirely.
  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Persistent SDK error banner */}
      {atClient.initError && (
        <ErrorBanner message={atClient.initError} onRetry={atClient.retryInit} />
      )}

      {/* Network degraded / offline banner.
          - Suppressed when ErrorBanner is already shown (initError takes priority).
          - Suppressed during the very first SDK initialisation (normal app load) to
            avoid a flash; shown for all subsequent degraded/poor states so the user
            always knows if a number-switch reconnect is struggling. */}
      {!atClient.initError && sdkEverReadyRef.current && (
        <NetworkBanner
          networkQuality={atClient.networkQuality}
          sdkStatus={atClient.sdkStatus}
          onReconnect={atClient.retryInit}
        />
      )}

      {/* Header: number selector + headset / network quality indicator */}
      <DialerHeader
        numbers={numbers}
        selectedNumberId={selectedNumberId}
        onSelectNumber={setSelectedNumberId}
        networkQuality={atClient.networkQuality}
        showNumberSelector={activeTab === "dialpad"}
      />

      {/* Content area — priority order:
            1. IncomingCallScreen (inbound ringing, waiting for rep to answer/decline)
            2. ActiveCallScreen   (call in progress or outbound ringing)
            3. PostCallScreen     (wrap-up after call ends — answered calls only)
            4. Normal tab screens
          All tab screens stay mounted (visibility toggled) so state is never lost. */}
      <div className="relative flex flex-1 flex-col overflow-hidden">
        {isIncomingRinging && atClient.incomingCall ? (
          /* ── Incoming call screen ── */
          <IncomingCallScreen
            incomingCall={atClient.incomingCall}
            onAnswer={handleAnswerIncoming}
            onDecline={handleDeclineIncoming}
          />
        ) : isActiveCalling && atClient.activeCall ? (
          /* ── In-call screen ── */
          <ActiveCallScreen
            isRinging={atClient.sdkStatus === "ringing"}
            isInCall={atClient.sdkStatus === "incall"}
            activeCall={atClient.activeCall}
            isMuted={atClient.isMuted}
            isOnHold={atClient.isOnHold}
            outboundNumber={selectedNumber?.number ?? null}
            apiUrl={API_URL}
            accessToken={session.access_token}
            onMute={atClient.mute}
            onUnmute={atClient.unmute}
            onHold={atClient.hold}
            onUnhold={atClient.unhold}
          />
        ) : atClient.lastEndedCall ? (
          /* ── Post-call wrap-up screen ── */
          <PostCallScreen
            call={atClient.lastEndedCall}
            accessToken={session.access_token}
            apiUrl={API_URL}
            wrapupTimeSeconds={wrapupTimeSeconds}
            onDone={() => {
              atClient.clearLastEndedCall();
              setActiveTab("dialpad");
            }}
          />
        ) : (
          /* ── Normal tab screens ── */
          <>
            <div className={activeTab === "history" ? "flex flex-1 flex-col min-h-0" : "hidden"}>
              <CallHistoryScreen
                accessToken={session.access_token}
                apiUrl={API_URL}
                onCall={(number) => atClient.call(number)}
                isActive={activeTab === "history"}
              />
            </div>
            <div className={activeTab === "contacts" ? "flex flex-1 flex-col min-h-0" : "hidden"}>
              <ContactsScreen
                accessToken={session.access_token}
                apiUrl={API_URL}
                onCall={(number) => atClient.call(number)}
                isActive={activeTab === "contacts"}
              />
            </div>
            <div className={activeTab === "dialpad" ? "flex flex-1 flex-col" : "hidden"}>
              <DialpadScreen
                sdkStatus={atClient.sdkStatus}
                value={dialValue}
                onChange={setDialValue}
                countryCode={dialCountryCode}
                onCountryCodeChange={setDialCountryCode}
                isActive={activeTab === "dialpad"}
              />
            </div>
            <div className={activeTab === "messages" ? "flex flex-1 flex-col" : "hidden"}>
              <MessagesScreen />
            </div>
            <div className={activeTab === "profile" ? "flex flex-1 flex-col" : "hidden"}>
              <ProfileScreen
                firstName={profileFirstName}
                lastName={profileLastName}
                role={profileRole}
                email={session.user.email ?? null}
                plan={planStatus}
                creditBalance={creditBalance}
              />
            </div>
          </>
        )}
      </div>

      {/* Tab bar — hidden during incoming calls and post-call wrap-up. */}
      {!isIncomingRinging && !atClient.lastEndedCall && (
        <TabBar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          canDialCall={canDialCall}
          onDialCall={() => void handleDialCall()}
          isInCall={isActiveCalling}
          onHangup={atClient.hangup}
          disabledTabs={["messages"]}
        />
      )}

      {/* ── Do Not Call confirmation modal ─────────────────────────────────── */}
      {dncConfirm && (
        <div className="absolute inset-0 z-50 flex items-end justify-center bg-black/40 pb-4">
          <div className="mx-3 w-full max-w-[400px] overflow-hidden rounded-2xl bg-card shadow-xl">
            <div className="flex flex-col items-center gap-3 px-6 py-5">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <PhoneOff className="h-6 w-6 text-destructive" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground">Do Not Call</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{dncConfirm.contactName}</span> has
                  Do Not Call enabled. Are you sure you want to proceed?
                </p>
              </div>
            </div>
            <div className="flex gap-2 border-t border-border px-4 py-3">
              <button
                onClick={() => setDncConfirm(null)}
                className="flex-1 rounded-lg border border-border py-2.5 text-sm font-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const number = dncConfirm.callNumber;
                  setDncConfirm(null);
                  atClient.call(number);
                }}
                className="flex-1 rounded-lg bg-destructive py-2.5 text-sm font-semibold text-destructive-foreground"
              >
                Call anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
